import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, RefreshCw, Loader2, MessageSquareReply, Settings2, Inbox } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Skeleton } from "./ui/skeleton";
import { api, formatDateTime } from "../lib/api";

/**
 * Statuskort for svar-sporing (kun plattform-admin): MX-post, Brevo-webhook, webhook-nøkkel og API-nøkkel.
 * Bruker den mellomlagrede helsesjekken (10 min) – «Sjekk nå» tvinger en ny sjekk.
 */
export const ReplyTrackingHealthCard = () => {
  const navigate = useNavigate();
  const [health, setHealth] = useState(null);
  const [error, setError] = useState(false);
  const [checking, setChecking] = useState(false);

  const load = useCallback(async (refresh = false) => {
    if (refresh) setChecking(true);
    try {
      const res = await api.get("/admin/prospects/replies/health", { params: refresh ? { refresh: "1" } : {} });
      setHealth(res.data);
      setError(false);
    } catch {
      setError(true);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    load(false);
  }, [load]);

  const openSetup = () => navigate("/dashboard/kontaktregister-admin?oppsett=1");

  if (health === null && !error) {
    return (
      <Card className="border-border" data-testid="reply-tracking-health-loading">
        <CardContent className="p-5 space-y-3">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </CardContent>
      </Card>
    );
  }

  const status = error ? "error" : health.status;
  const statusBadge =
    status === "ok" ? (
      <Badge className="bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] hover:bg-[hsl(var(--success))]" data-testid="reply-tracking-health-status">Alt i orden</Badge>
    ) : status === "off" ? (
      <Badge variant="secondary" data-testid="reply-tracking-health-status">Ikke slått på</Badge>
    ) : (
      <Badge className="bg-[hsl(var(--danger))] text-[hsl(var(--danger-foreground))] hover:bg-[hsl(var(--danger))]" data-testid="reply-tracking-health-status">
        {error ? "Kunne ikke sjekke" : `${health.problems.length} ${health.problems.length === 1 ? "problem" : "problemer"}`}
      </Badge>
    );

  return (
    <Card className={`border-border ${status === "error" ? "border-[hsl(var(--danger))]/40" : ""}`} data-testid="reply-tracking-health-card">
      <CardContent className="p-5">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-brand-soft text-brand shrink-0"><MessageSquareReply className="w-4 h-4" /></span>
              <h2 className="font-heading font-semibold text-base">Svar-sporing</h2>
              {statusBadge}
            </div>
            <p className="text-sm text-muted-foreground mt-1.5">
              {status === "off"
                ? "Svar på e-poster fra kontaktregisteret går rett til admin. Slå på sporing for å få dem i innboksen og merke organisasjoner «Interessert» automatisk."
                : error
                  ? "Fikk ikke kontakt med serveren. Prøv igjen om litt."
                  : `Svar til svar+…@${health.domain} fanges opp, knyttes til organisasjonen og videresendes til deg.`}
            </p>
            {status !== "off" && !error && (
              <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm" data-testid="reply-tracking-health-checks">
                {health.checks.map((c) => (
                  <li key={c.key} className="flex items-start gap-2" data-testid={`reply-tracking-health-check-${c.key}`} data-ok={c.ok ? "1" : "0"}>
                    {c.ok ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-[hsl(var(--success))]" /> : <XCircle className="w-4 h-4 mt-0.5 shrink-0 text-[hsl(var(--danger))]" />}
                    <span className="min-w-0">
                      <span className="font-medium">{c.label}</span>
                      <span className="block text-xs text-muted-foreground break-words">{c.detail}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {!error && health.checked_at && (
              <p className="text-xs text-muted-foreground mt-3" data-testid="reply-tracking-health-checked-at">
                Sist sjekket {formatDateTime(health.checked_at)}{health.cached ? " (mellomlagret, sjekkes på nytt hvert 10. minutt)" : ""}
              </p>
            )}
          </div>
          <div className="flex flex-row flex-wrap sm:flex-col gap-2 shrink-0">
            <Button size="sm" onClick={openSetup} data-testid="reply-tracking-health-open-setup">
              <Settings2 className="w-4 h-4 mr-1.5" /> Åpne oppsett
            </Button>
            {status !== "off" && (
              <Button size="sm" variant="secondary" onClick={() => navigate("/dashboard/svar")} data-testid="reply-tracking-health-open-inbox">
                <Inbox className="w-4 h-4 mr-1.5" /> Svar-innboks
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => load(true)} disabled={checking} data-testid="reply-tracking-health-refresh">
              {checking ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />} Sjekk nå
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
